import {
  Condition,
  ConditionGroup,
  SearchQueryBody,
  SimpleCondition,
  SortDirection,
} from "@/types";

import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";

import {
  ActionRecord,
  CancelActiveInvocationsResponse,
  CreateActionRequest,
  InvocationActivityRequest,
  InvocationActivityResponse,
  InvocationBreakdownRequest,
  InvocationBreakdownResponse,
  InvocationQueueStats,
  InvocationRecord,
  InvocationStatus,
  UpdateActionRequest,
} from "./ActionRecord";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
}

export type ActionQueryScope = "org" | "actionhub";

interface QueryActionsOptions {
  abortSignal: AbortSignal;
  orgId: string;
  pageToken: string;
  limit: number;
  /** Keeps only the actions matching it; the server filters before it pages. */
  condition: Condition;
  /** A column of the action record the server orders by; `created` when omitted. */
  sortBy: string;
  sortDirection: SortDirection;
}

export class ActionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createAction(
    request: CreateActionRequest,
    orgId: string,
  ): Promise<ActionRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/actions");

    const response = await this.#httpClient.post(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify(request),
    });

    return response.json<ActionRecord>();
  }

  public async deleteAction(
    name: string,
    options?: Partial<Options>,
  ): Promise<void> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "deleteAction requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/actions/${name}`);
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: false,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.throwIfError();
  }

  public async getAction(
    name: string,
    digest?: string,
    options?: Partial<Options>,
  ): Promise<ActionRecord> {
    if (!options?.resourceOwnerId) {
      throw Error("getAction requires a resource owner ID, none was provided");
    }

    const searchParams = new URLSearchParams();
    if (digest) {
      searchParams.append("digest", digest);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/${name}`,
      searchParams,
    );

    return this.#httpClient
      .get(requestUrl, {
        headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
        signal: options?.abortSignal,
      })
      .then((response) => response.json<ActionRecord>());
  }

  /**
   * The distinct tags on the actions in `scope`, sorted ascending: the
   * vocabulary a Tags filter offers. Read from the tags endpoints rather
   * than folded from a listing, so building a menu never fetches every action.
   */
  public async listTags(
    scope: ActionQueryScope,
    options: { orgId: string; abortSignal?: AbortSignal },
  ): Promise<string[]> {
    const requestUrl = this.#httpClient.constructUrl(
      scope === "actionhub"
        ? "v1/actions/collection-info/tags/actionhub"
        : "v1/actions/tags",
    );

    return this.#httpClient
      .get(requestUrl, {
        headers: robotoHeaders({ resourceOwnerId: options.orgId }),
        signal: options.abortSignal,
      })
      .then((response) => response.json<string[]>());
  }

  /**
   * Fetch every action visible in the given scope ("org" for the caller's
   * organization, "actionhub" for actions published to the Action Hub),
   * following `next_token` until the server reports no more pages.
   */
  public async listActions(
    scope: ActionQueryScope,
    options?: Partial<QueryActionsOptions>,
  ): Promise<ActionRecord[]> {
    const items: ActionRecord[] = [];
    let pageToken: string | undefined = options?.pageToken;

    while (true) {
      const page = await this.queryActions(scope, {
        ...options,
        pageToken,
      });

      items.push(...page.items);

      const nextToken = page.next_token ?? undefined;
      if (nextToken === undefined) {
        return items;
      }

      pageToken = nextToken;
    }
  }

  /**
   * Fetch one page of actions in the given scope ("org" for the caller's
   * organization, "actionhub" for actions published to the Action Hub).
   *
   * The server honors the whole specification: a page holds `limit` matching
   * actions in the requested order unless it is the last, and `next_token`
   * is present only when another page exists.
   */
  public async queryActions(
    scope: ActionQueryScope,
    options?: Partial<QueryActionsOptions>,
  ): Promise<PaginatedResponse<ActionRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      scope === "actionhub" ? "v1/actions/query/actionhub" : "v1/actions/query",
    );

    const body: SearchQueryBody = {};
    if (options?.pageToken !== undefined) {
      body.after = options.pageToken;
    }
    if (options?.condition !== undefined) {
      body.condition = options.condition;
    }
    if (options?.limit !== undefined) {
      body.limit = options.limit;
    }
    if (options?.sortBy !== undefined) {
      body.sort_by = options.sortBy;
      body.sort_direction = options.sortDirection;
    }

    const response = await this.#httpClient.post(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(body),
    });

    return response.json<PaginatedResponse<ActionRecord>>();
  }

  public async updateAction(
    name: string,
    updates: UpdateActionRequest,
    options?: Partial<Options>,
  ): Promise<ActionRecord> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "updateAction requires a resource owner ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(`v1/actions/${name}`);

    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
      body: JSON.stringify(updates),
    });

    return response.json<ActionRecord>();
  }

  public async countActiveInvocationsForDataset(
    orgId: string,
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<number> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/count/active`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();

    return Number.parseInt(await response.text(), 10);
  }

  public async countTerminalInvocationsForDataset(
    orgId: string,
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<number> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/invocations/count/terminal`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();

    return Number.parseInt(await response.text(), 10);
  }

  public async getInvocationQueueStats(
    orgId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      actionName: string;
      actionOrgId: string;
    }>,
  ): Promise<InvocationQueueStats> {
    const searchParams = new URLSearchParams();
    if (
      options?.actionName !== undefined &&
      options?.actionOrgId !== undefined
    ) {
      searchParams.append("action_name", options.actionName);
      searchParams.append("action_org_id", options.actionOrgId);
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/actions/invocations/queue-stats",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: orgId }),
      signal: options?.abortSignal,
    });

    return response.json<InvocationQueueStats>();
  }

  /** One page of invocations matching a query, in the query API's own vocabulary. */
  public async queryInvocations(
    query: SearchQueryBody,
    options: { orgId: string; abortSignal?: AbortSignal },
  ): Promise<PaginatedResponse<InvocationRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/actions/invocations/query",
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options.orgId }),
      signal: options.abortSignal,
      body: JSON.stringify(query),
    });

    return response.json<PaginatedResponse<InvocationRecord>>();
  }

  /** What the org's invocations (narrowed by `request.condition`) were doing across a window. */
  public async getInvocationActivity(
    orgId: string,
    request: InvocationActivityRequest,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<InvocationActivityResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/actions/invocations/activity",
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<InvocationActivityResponse>();
  }

  /** The org's invocations currently in some statuses, grouped along one or more dimensions. */
  public async getInvocationBreakdown(
    orgId: string,
    request: InvocationBreakdownRequest,
    options?: Partial<{ abortSignal: AbortSignal }>,
  ): Promise<InvocationBreakdownResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/actions/invocations/breakdown",
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return response.json<InvocationBreakdownResponse>();
  }

  public async queryActiveInvocationsForDataset(
    orgId: string,
    datasetId: string,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const datasetCondition: SimpleCondition = {
      field: "data_source_id",
      comparator: "EQUALS",
      value: datasetId,
    };
    return this.queryActiveInvocations(orgId, datasetCondition, signal);
  }

  /**
   * The first page of the invocations `scope` selects that are still queued or running.
   */
  public async queryActiveInvocations(
    orgId: string,
    scope: Condition,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/query`,
    );

    const conditions = [
      InvocationStatus.Completed,
      InvocationStatus.Cancelled,
      InvocationStatus.Failed,
      InvocationStatus.Deadly,
    ].map((status) => {
      const condition: SimpleCondition = {
        field: "last_status",
        comparator: "NOT_EQUALS",
        value: status,
      };

      return condition;
    });

    const nonTerminalCondition: ConditionGroup = {
      conditions: conditions,
      operator: "AND",
    };

    const searchConditions: ConditionGroup = {
      conditions: [scope, nonTerminalCondition],
      operator: "AND",
    };

    const searchBody: SearchQueryBody = {
      condition: searchConditions,
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal,
      body: JSON.stringify(searchBody),
    });

    return await response.json<{
      items: InvocationRecord[];
      next_token: string | null;
    } | null>();
  }

  /**
   * Cancel up to one batch of the org's active invocations that `scope` selects. Repeat while the
   * response's `has_more` is true.
   *
   * `scope` is required: the endpoint cancels every active invocation in the org when it is sent
   * without one.
   */
  public async cancelActiveInvocations(
    orgId: string,
    scope: Condition,
  ): Promise<CancelActiveInvocationsResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/actions/invocations/cancel/all",
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      body: JSON.stringify({ condition: scope }),
    });

    return response.json<CancelActiveInvocationsResponse>();
  }

  public async queryRecentInvocationsForDataset(
    orgId: string,
    datasetId: string,
    signal: AbortSignal,
  ): Promise<{ items: InvocationRecord[]; next_token: string | null } | null> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/query`,
    );

    const datasetCondition: SimpleCondition = {
      field: "data_source_id",
      comparator: "EQUALS",
      value: datasetId,
    };

    const searchConditions: ConditionGroup = {
      conditions: [datasetCondition],
      operator: "AND",
    };

    const searchBody: SearchQueryBody = {
      condition: searchConditions,
      sort_by: "created",
      sort_direction: "DESC",
      // Limit results to avoid fetching too many invocations
      limit: 50,
    };

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal,
      body: JSON.stringify(searchBody),
    });

    return response.json<{
      items: InvocationRecord[];
      next_token: string | null;
    } | null>();
  }

  public async getInvocation(
    invocationId: string,
    orgId?: string,
    options?: Partial<Options>,
  ): Promise<InvocationRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/actions/invocations/${invocationId}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ orgId }),
      signal: options?.abortSignal,
    });

    return response.json<InvocationRecord>();
  }
}
